/**
 * Whether a person arriving in the app is sent to connect their monday
 * account (OAuth), so the server can copy template files for them later.
 *
 * The iframe navigates itself to the server's /oauth/start?return=app, which
 * sends it to monday's approval page; the callback brings it back into the
 * app (SmartForm does the same). Never sent when:
 *   - the account has not approved the connection's scopes for the app
 *     (lib/permissionCheck CONNECT_SCOPES): monday would answer "Invalid
 *     scope" and strand them on its page; the permission alert asks an admin
 *   - the person is a viewer (they cannot apply)
 *   - the status could not be read (an outage must not trap them in a loop;
 *     the apply-time Connect dialog still covers them)
 *   - they just came back from a refused approval (?oauth=denied), or were
 *     sent there in the last REDIRECT_COOLDOWN_MS — then a Connect banner
 *     shows instead, so declining never loops
 */
export const REDIRECT_COOLDOWN_MS = 10 * 60 * 1000;
const KEY = 'spt-oauth-redirected-at';

/**
 * @returns 'none' (nothing to do) | 'redirect' (send the iframe to connect) |
 *          'banner' (not connected, but do not send them again now)
 */
export function arrivalAction({ isViewOnly, connectBlocked, status, search, lastRedirectAt, now }) {
  if (isViewOnly || connectBlocked) return 'none';
  if (!status || status.error || typeof status.connected !== 'boolean') return 'none';
  if (status.connected) return 'none';
  if (new URLSearchParams(search || '').get('oauth') === 'denied') return 'banner';
  if (lastRedirectAt && now - lastRedirectAt < REDIRECT_COOLDOWN_MS) return 'banner';
  return 'redirect';
}

/** sessionStorage can throw (blocked storage, previews): every access is guarded. */
export function lastRedirectAt() {
  try {
    return Number(window.sessionStorage.getItem(KEY)) || 0;
  } catch (e) {
    return 0;
  }
}

export function rememberRedirect(now) {
  try {
    window.sessionStorage.setItem(KEY, String(now));
  } catch (e) {
    // no storage: the cooldown is off, ?oauth=denied still stops a loop
  }
}

export function forgetRedirect() {
  try {
    window.sessionStorage.removeItem(KEY);
  } catch (e) {
    // nothing to forget
  }
}
